import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, BRAND_BANNER } from "@/lib/api";
import {
  googleSignIn, makeRecaptcha, phoneSendCode, phoneConfirm,
  emailSendLink, isEmailLink, storedEmail, emailCompleteSignIn, humanizeFirebaseError,
} from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PublicLayout from "@/components/PublicLayout";
import {
  ArrowRight, GpsFix, Sparkle, Leaf, GoogleLogo, Phone, EnvelopeSimple,
  CaretLeft, ShieldCheck, User, Buildings,
} from "@phosphor-icons/react";

const SKY = "https://images.unsplash.com/photo-1599209248411-5124adbb1da2?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const PULSE_SITES = [
  { name: "SocialPulse", url: "https://socialpulse.com" },
  { name: "AlumniPulse", url: "https://alumnipulse.net" },
  { name: "TravelerPulse", url: "https://travelerpulse.pro" },
  { name: "FoodPulse", url: "https://foodpulse.net" },
  { name: "NaturePulse", url: "https://naturepulse.net" },
  { name: "FamilyTree", url: "https://familytree.team" },
];

export default function Login() {
  const { login, firebaseSession } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [mode, setMode] = useState("home"); // home | phone | email | legacy

  // account type for sign-up (applied on first federated login)
  const [accountType, setAccountType] = useState("individual"); // individual | business
  const [businessName, setBusinessName] = useState("");
  const acct = () => ({ account_type: accountType, business_name: accountType === "business" ? businessName : "" });

  // phone state
  const [phone, setPhone] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [otp, setOtp] = useState("");
  // email state
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [pendingEmailLink, setPendingEmailLink] = useState(false);
  // legacy
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPass, setAdminPass] = useState("");
  // forgot-password
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  // Detect an incoming email magic-link WITHOUT consuming it (Gmail scanner safe).
  useEffect(() => {
    try {
      if (isEmailLink()) {
        setMode("email");
        setPendingEmailLink(true);
        setEmail(storedEmail());
      }
    } catch (e) { /* firebase not ready */ }
  }, []);

  const finish = (r) => {
    setBusy("");
    if (r.ok) {
      const next = new URLSearchParams(window.location.search).get("next");
      nav(next || "/dashboard");
    } else setError(r.error || "Sign-in failed.");
  };

  const doGoogle = async () => {
    setError(""); setBusy("google");
    try {
      const idToken = await googleSignIn();
      finish(await firebaseSession(idToken, acct()));
    } catch (e) { setBusy(""); setError(humanizeFirebaseError(e)); }
  };

  const sendOtp = async (e) => {
    e.preventDefault(); setError(""); setBusy("phone");
    try {
      const verifier = window.__ppRecaptcha || (window.__ppRecaptcha = makeRecaptcha("recaptcha-container"));
      const conf = await phoneSendCode(phone.trim(), verifier);
      setConfirmation(conf); setBusy("");
    } catch (e) {
      setBusy(""); setError(humanizeFirebaseError(e));
      if (window.__ppRecaptcha) { try { window.__ppRecaptcha.clear(); } catch (_) {} window.__ppRecaptcha = null; }
    }
  };
  const verifyOtp = async (e) => {
    e.preventDefault(); setError(""); setBusy("phone");
    try {
      const idToken = await phoneConfirm(confirmation, otp.trim());
      finish(await firebaseSession(idToken, acct()));
    } catch (e) { setBusy(""); setError(humanizeFirebaseError(e)); }
  };

  const sendEmailLink = async (e) => {
    e.preventDefault(); setError(""); setBusy("email");
    try {
      await emailSendLink(email.trim());
      setEmailSent(true); setBusy("");
    } catch (e) { setBusy(""); setError(humanizeFirebaseError(e)); }
  };
  const completeEmailLink = async () => {
    setError(""); setBusy("email");
    try {
      const idToken = await emailCompleteSignIn(email.trim());
      finish(await firebaseSession(idToken, acct()));
    } catch (e) { setBusy(""); setError(humanizeFirebaseError(e)); }
  };

  const doLegacy = async (e) => {
    e.preventDefault(); setError(""); setBusy("legacy");
    finish(await login(adminEmail, adminPass));
  };

  const doForgot = async (e) => {
    e.preventDefault(); setError(""); setBusy("forgot");
    try {
      await api.post("/auth/forgot-password", { email: resetEmail.trim().toLowerCase() });
    } catch { /* never reveal whether the email exists */ }
    setResetSent(true); setBusy("");
  };

  const back = () => { setMode("home"); setError(""); setConfirmation(null); setEmailSent(false); setPendingEmailLink(false); setResetSent(false); setResetEmail(""); };

  return (
    <PublicLayout>
    <div className="relative overflow-hidden">
      <img src={SKY} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-sky-200/70 via-sky-100/60 to-emerald-100/70" />

      <div className="relative mx-auto flex max-w-5xl flex-col items-center justify-center px-5 py-14">
        <div className="w-full overflow-hidden rounded-xl border border-white/60 shadow-xl ring-1 ring-black/5">
          <img src={BRAND_BANNER} alt="PhotoPulse.pro — Professional Imaging Solutions" className="w-full object-cover" />
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {[{ i: ShieldCheck, t: "Private by design · you choose what's shared" }, { i: GpsFix, t: "GPS + AI Geolocation" }, { i: Sparkle, t: "AI Vision & OCR" }, { i: Leaf, t: "Species Field Guide" }].map((f, k) => (
            <span key={k} className="flex items-center gap-2 rounded-full border border-white/70 bg-white/70 px-4 py-2 text-sm font-medium text-zinc-700 backdrop-blur-sm">
              <f.i size={16} weight="fill" className="text-[#F27A54]" /> {f.t}
            </span>
          ))}
        </div>

        <div className="mt-8 w-full max-w-sm rounded-xl border border-white/80 bg-white/90 p-7 shadow-2xl ring-1 ring-black/5 backdrop-blur-md">
          <div className="flex items-center gap-2">
            {mode !== "home" && (
              <button data-testid="login-back" onClick={back} className="grid h-7 w-7 place-items-center rounded-md text-zinc-500 hover:bg-zinc-100">
                <CaretLeft size={16} weight="bold" />
              </button>
            )}
            <div>
              <p className="overline text-[#F27A54]">One account · every Pulse site</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight text-zinc-900">Sign in</h2>
            </div>
          </div>
          <p className="mt-1 text-sm text-zinc-500">Use the same login you have on any SocialPulse-family site.</p>

          {/* Account type — applied when you sign up (first login) */}
          {mode !== "legacy" && (
            <div className="mt-5">
              <Label className="overline text-[10px] text-zinc-500">I'm signing up as</Label>
              <div data-testid="account-type-toggle" className="mt-1.5 grid grid-cols-2 gap-2">
                <button type="button" data-testid="account-type-individual" onClick={() => setAccountType("individual")}
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                    accountType === "individual" ? "border-[#F27A54] bg-blue-50 text-[#F27A54]" : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
                  }`}>
                  <User size={16} weight="fill" /> Individual
                </button>
                <button type="button" data-testid="account-type-business" onClick={() => setAccountType("business")}
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                    accountType === "business" ? "border-[#F27A54] bg-blue-50 text-[#F27A54]" : "border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
                  }`}>
                  <Buildings size={16} weight="fill" /> Business
                </button>
              </div>
              {accountType === "business" && (
                <Input data-testid="business-name-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Business / organization name" className="mt-2 h-11 rounded-md" />
              )}
            </div>
          )}

          {/* HOME — method chooser */}
          {mode === "home" && (
            <div className="mt-6 space-y-3">
              <button data-testid="login-google" onClick={doGoogle} disabled={!!busy}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-md border border-zinc-300 bg-white font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60">
                <GoogleLogo size={18} weight="bold" className="text-[#EA4335]" />
                {busy === "google" ? "Connecting…" : "Continue with Google"}
              </button>
              <button data-testid="login-phone-open" onClick={() => setMode("phone")} disabled={!!busy}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-md border border-zinc-300 bg-white font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60">
                <Phone size={18} weight="fill" className="text-[#F27A54]" /> Continue with Phone
              </button>
              <button data-testid="login-email-open" onClick={() => setMode("email")} disabled={!!busy}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-md border border-zinc-300 bg-white font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60">
                <EnvelopeSimple size={18} weight="fill" className="text-[#F27A54]" /> Email me a magic link
              </button>
              <button data-testid="login-legacy-open" onClick={() => setMode("legacy")}
                className="flex w-full items-center justify-center gap-1.5 pt-1 text-xs font-medium text-zinc-500 hover:text-[#F27A54]">
                <ShieldCheck size={13} weight="fill" /> Admin sign-in
              </button>
            </div>
          )}

          {/* PHONE */}
          {mode === "phone" && (
            <div className="mt-6">
              {!confirmation ? (
                <form onSubmit={sendOtp} className="space-y-4">
                  <div>
                    <Label className="overline text-[10px] text-zinc-500">Phone number</Label>
                    <Input data-testid="login-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 555 123 4567" className="mt-1.5 h-11 rounded-md" required />
                    <p className="mt-1 text-xs text-zinc-400">Include country code, e.g. +1.</p>
                  </div>
                  <Button data-testid="login-phone-send" type="submit" disabled={busy === "phone"}
                    className="h-11 w-full gap-2 rounded-md bg-[#F27A54] font-semibold text-white hover:bg-[#d9633f]">
                    {busy === "phone" ? "Sending…" : "Send code"} <ArrowRight size={16} weight="bold" />
                  </Button>
                </form>
              ) : (
                <form onSubmit={verifyOtp} className="space-y-4">
                  <div>
                    <Label className="overline text-[10px] text-zinc-500">Enter the 6-digit code</Label>
                    <Input data-testid="login-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)}
                      inputMode="numeric" placeholder="123456" className="mt-1.5 h-11 rounded-md tracking-[0.4em]" required />
                  </div>
                  <Button data-testid="login-otp-verify" type="submit" disabled={busy === "phone"}
                    className="h-11 w-full gap-2 rounded-md bg-[#F27A54] font-semibold text-white hover:bg-[#d9633f]">
                    {busy === "phone" ? "Verifying…" : "Verify & sign in"} <ArrowRight size={16} weight="bold" />
                  </Button>
                </form>
              )}
            </div>
          )}

          {/* EMAIL LINK */}
          {mode === "email" && (
            <div className="mt-6">
              {pendingEmailLink ? (
                <div className="space-y-4">
                  <p className="rounded-md bg-blue-50 px-3 py-2 text-sm text-[#F27A54]">
                    You opened a sign-in link. Confirm your email to finish.
                  </p>
                  <div>
                    <Label className="overline text-[10px] text-zinc-500">Your email</Label>
                    <Input data-testid="login-email-confirm-input" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-md" required />
                  </div>
                  <Button data-testid="login-email-confirm" onClick={completeEmailLink} disabled={busy === "email" || !email}
                    className="h-11 w-full gap-2 rounded-md bg-[#F27A54] font-semibold text-white hover:bg-[#d9633f]">
                    {busy === "email" ? "Signing in…" : "Confirm & sign in"} <ArrowRight size={16} weight="bold" />
                  </Button>
                </div>
              ) : emailSent ? (
                <div className="space-y-3 text-center">
                  <EnvelopeSimple size={40} weight="thin" className="mx-auto text-[#F27A54]" />
                  <p className="text-sm font-semibold text-zinc-800">Check your inbox</p>
                  <p className="text-sm text-zinc-500">We sent a sign-in link to <span className="font-medium">{email}</span>. Open it on this device and click confirm.</p>
                </div>
              ) : (
                <form onSubmit={sendEmailLink} className="space-y-4">
                  <div>
                    <Label className="overline text-[10px] text-zinc-500">Email address</Label>
                    <Input data-testid="login-email-input" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-11 rounded-md" required />
                  </div>
                  <Button data-testid="login-email-send" type="submit" disabled={busy === "email"}
                    className="h-11 w-full gap-2 rounded-md bg-[#F27A54] font-semibold text-white hover:bg-[#d9633f]">
                    {busy === "email" ? "Sending…" : "Send magic link"} <ArrowRight size={16} weight="bold" />
                  </Button>
                </form>
              )}
            </div>
          )}

          {/* LEGACY ADMIN */}
          {mode === "legacy" && (
            <form onSubmit={doLegacy} className="mt-6 space-y-4">
              <div>
                <Label className="overline text-[10px] text-zinc-500">Email</Label>
                <Input data-testid="login-email" type="email" value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)} className="mt-1.5 h-11 rounded-md" required />
              </div>
              <div>
                <Label className="overline text-[10px] text-zinc-500">Password</Label>
                <Input data-testid="login-password" type="password" value={adminPass}
                  onChange={(e) => setAdminPass(e.target.value)} className="mt-1.5 h-11 rounded-md" required />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={busy === "legacy"}
                className="h-11 w-full gap-2 rounded-md bg-[#F27A54] font-semibold text-white hover:bg-[#d9633f]">
                {busy === "legacy" ? "Signing in…" : "Enter Control Center"} <ArrowRight size={16} weight="bold" />
              </Button>
              <button type="button" data-testid="forgot-password-open" onClick={() => { setMode("forgot"); setError(""); }}
                className="mx-auto block text-xs font-semibold text-[#F27A54] hover:underline">
                Forgot password?
              </button>
            </form>
          )}

          {mode === "forgot" && (
            resetSent ? (
              <div className="mt-6 text-center" data-testid="forgot-sent">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-[#2D6A4F]">
                  <EnvelopeSimple size={22} weight="fill" />
                </div>
                <p className="mt-3 text-sm text-zinc-700">
                  If an account exists for <b>{resetEmail}</b>, we've emailed a password-reset link. It expires in 1 hour.
                </p>
                <button type="button" onClick={() => setMode("legacy")} data-testid="forgot-back-signin"
                  className="mt-4 text-xs font-semibold text-[#F27A54] hover:underline">Back to sign in</button>
              </div>
            ) : (
              <form onSubmit={doForgot} className="mt-6 space-y-4" data-testid="forgot-form">
                <p className="text-sm text-zinc-600">Enter your account email and we'll send you a link to reset your password.</p>
                <div>
                  <Label className="overline text-[10px] text-zinc-500">Email</Label>
                  <Input data-testid="forgot-email" type="email" value={resetEmail} required
                    onChange={(e) => setResetEmail(e.target.value)} className="mt-1.5 h-11 rounded-md" placeholder="you@example.com" />
                </div>
                <Button data-testid="forgot-submit" type="submit" disabled={busy === "forgot"}
                  className="h-11 w-full gap-2 rounded-md bg-[#2D6A4F] font-semibold text-white hover:bg-[#245840]">
                  {busy === "forgot" ? "Sending…" : "Send reset link"} <EnvelopeSimple size={16} weight="fill" />
                </Button>
                <button type="button" onClick={() => setMode("legacy")}
                  className="mx-auto block text-xs font-semibold text-[#F27A54] hover:underline">Back to sign in</button>
              </form>
            )
          )}

          {error && <p data-testid="login-error" className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

          <p className="mt-5 text-center text-xs text-zinc-500">
            <a href="/terms" className="font-medium text-[#F27A54] hover:underline">Terms of Service</a>
            <span className="mx-1.5 text-zinc-300">·</span>
            <a href="/privacy" className="font-medium text-[#F27A54] hover:underline">Privacy Policy</a>
          </p>
          <div id="recaptcha-container" />
        </div>

        <div className="mt-7 w-full max-w-2xl text-center">
          <p className="overline text-zinc-500">Feeds the entire Pulse network</p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
            {PULSE_SITES.map((s) => (
              <a key={s.name} href={s.url} target="_blank" rel="noreferrer"
                data-testid={`pulse-link-${s.name.toLowerCase()}`}
                className="text-sm font-semibold text-[#F27A54] underline-offset-2 hover:underline">
                {s.name}
              </a>
            ))}
          </div>
        </div>

        <p className="mt-6 max-w-lg text-center text-sm font-semibold text-zinc-800">
          PhotoPulse.pro™ — Turn Every Photo into Intelligent, Location-Aware Content.
        </p>
      </div>
    </div>
    </PublicLayout>
  );
}
